import React, { useState } from "react";
import type { ApolloClient } from "@apollo/client";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { toast } from "react-toastify";
import Button from "components/Button";
import { createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import {
  processRoster,
  updateProgressiveOnboarding,
} from "actions/employerActions";
import {
  progressiveOnboardingEvents,
  progressiveOnboardingStates,
} from "statics/states";
import { ConnectionActive } from "statics/payrollIntegrationStates";
import type { RootState } from "types/state";

const loadingSelector = createLoadingSelector([
  employerConstants.UPDATE_PROGRESSIVE_ONBOARDING,
  employerConstants.PROCESS_ROSTER,
]);

const mapStateToProps = (state: RootState) => ({
  isLaunching: loadingSelector(state),
});

const mapDispatchToProps = {
  updateProgressiveOnboarding,
  processRoster,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId: string;
  progressiveOnboardingState?: string | null;
  payrollIntegrationStatus?: string | null;
  hasPayrollIntegration: boolean;
  onSuccess: (progressiveOnboardingState: string) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

// LaunchPlan only transitions out of InvitedEmployees; from any other state the
// event is ignored but the backend still launches the plan state machine.
export const canLaunchPlan = (
  progressiveOnboardingState: string | null | undefined,
  payrollIntegrationStatus: string | null | undefined,
  hasPayrollIntegration: boolean
) =>
  progressiveOnboardingState === progressiveOnboardingStates.InvitedEmployees &&
  (hasPayrollIntegration
    ? payrollIntegrationStatus === ConnectionActive
    : !payrollIntegrationStatus ||
      payrollIntegrationStatus === ConnectionActive);

export const ProgressiveOnboardingStateField = ({
  companyId,
  progressiveOnboardingState,
  payrollIntegrationStatus,
  hasPayrollIntegration,
  updateProgressiveOnboarding,
  processRoster,
  client,
  isLaunching,
  onSuccess,
}: Props) => {
  const [showConfirm, setShowConfirm] = useState(false);

  if (
    !canLaunchPlan(
      progressiveOnboardingState,
      payrollIntegrationStatus,
      hasPayrollIntegration
    )
  ) {
    return <span>{progressiveOnboardingState}</span>;
  }

  const launchPlan = async () => {
    const result = (await updateProgressiveOnboarding(
      client as ApolloClient<object>,
      progressiveOnboardingEvents.LaunchPlan,
      companyId
    )) as {
      type: string;
      data?: string | null;
      error?: string;
      success: boolean;
    };
    if (!result.success) {
      setShowConfirm(false);
      toast.error(result.error || "Failed to launch plan.");
      return;
    }

    let rosterError: string | null = null;
    if (hasPayrollIntegration) {
      const rosterResult = (await processRoster(
        client as ApolloClient<object>,
        companyId
      )) as { type: string; error?: string; success: boolean };
      if (!rosterResult.success) {
        rosterError =
          rosterResult.error || "Plan launched, but the roster failed to sync.";
      }
    }

    setShowConfirm(false);
    if (rosterError) {
      toast.error(rosterError);
    } else {
      toast.success("Plan launched successfully");
    }
    if (result.data) {
      onSuccess(result.data);
    }
  };

  return (
    <>
      {progressiveOnboardingState} -{" "}
      <OverlayTrigger
        rootClose
        trigger="click"
        placement="bottom"
        show={showConfirm}
        onToggle={setShowConfirm}
        overlay={
          <Popover className="action-popover">
            <Popover.Body>
              <p>Are you sure you want to launch this plan?</p>
              <div>
                <Button
                  type="button"
                  color="cancel"
                  name="cancel"
                  btnLabel="Cancel"
                  onClick={() => setShowConfirm(false)}
                  size="sm"
                />
                <Button
                  type="button"
                  color="primary"
                  name="submit"
                  btnLabel="Launch"
                  withArrow={true}
                  loading={isLaunching}
                  onClick={() => void launchPlan()}
                  size="sm"
                />
              </div>
            </Popover.Body>
          </Popover>
        }
      >
        <button
          type="button"
          className="btn btn-link p-0 align-baseline"
          style={{ fontSize: 12, color: "#0cb7c4" }}
        >
          Launch Plan
        </button>
      </OverlayTrigger>
    </>
  );
};

export default connector(withApollo<Props>(ProgressiveOnboardingStateField));
